import { z } from 'zod';
/**
 * Stages of a copy the engine times, in pipeline order. Codes are stable (engine
 * `crates/engine/src/metrics.rs` `Stage::code`); labels are for people.
 */
export const HOT_PATH_STAGE_LABEL = {
    /** Log notification received → decoded signal taken in (pump.fun and PumpSwap). */
    decode: 'Decode',
    /** Other venues: the transaction is fetched after a deliberate delay and read from balances. */
    fetch: 'Fetch and decode',
    /** Signal taken in → order ready: copy engine, planner, venue and quote. Copies only. */
    plan: 'Plan',
    /** Order ready → transaction built and signed (live, native routes). */
    build: 'Build and sign',
    /** Signed → accepted by RPC `sendTransaction`. */
    send_rpc: 'Send via RPC',
    /** Signed → accepted by Helius Sender. */
    send_helius: 'Send via Helius Sender',
    /** Signed → Jupiter `/execute` answered (Jupiter lands the transaction before answering). */
    send_jupiter: 'Send via Jupiter',
    /** Sent → confirmed (live, native routes; off the hot path). */
    confirm: 'Confirm',
    /** Leader trade seen → our transaction sent (live) or filled (paper). Copies only. */
    total: 'Seen to sent',
};
export const HOT_PATH_STAGES = Object.keys(HOT_PATH_STAGE_LABEL);
const zMilliseconds = z.number().nonnegative();
/** One stage over one period. Percentiles are `null` while `count` is 0. */
export const zStageTimings = z.object({
    count: z.number().int().nonnegative(),
    p50Ms: zMilliseconds.nullable(),
    p95Ms: zMilliseconds.nullable(),
    p99Ms: zMilliseconds.nullable(),
    maxMs: zMilliseconds.nullable(),
});
export const zHotPathStage = z.object({
    stage: z.enum(HOT_PATH_STAGES),
    /** The last `windowMinutes` (rolled every minute). */
    window: zStageTimings,
    sinceStart: zStageTimings,
});
/**
 * `hotPath` of the engine heartbeat (Valkey `engine:status`), as the API passes it on. A stage
 * this version does not know (from a newer engine) is dropped rather than failing the whole report.
 */
export const zHotPath = z.object({
    windowMinutes: z.number().int().positive(),
    /** Seconds since the engine started (the "since start" period). */
    uptimeSeconds: z.number().int().nonnegative(),
    stages: z.array(z.unknown()).transform((stages) => stages.flatMap((stage) => {
        const parsed = zHotPathStage.safeParse(stage);
        return parsed.success ? [parsed.data] : [];
    })),
});
/**
 * How often the engine samples network priority fees (`crates/engine/src/fees.rs`): `active` while
 * dynamic fees are in use (every 1.5 s), `idle` otherwise (every 30 s), `backoff` while the RPC is
 * failing or rate-limiting (exponentially slower).
 */
export const PRIORITY_FEE_SAMPLING = ['active', 'idle', 'backoff'];
const zCount = z.number().int().nonnegative();
/** One venue's priority-fee estimate. The value fields are `null` before its first good sample. */
export const zVenuePriorityFee = z.object({
    /** Compute-unit price, micro-lamports per CU. */
    microLamportsPerCu: zCount.nullable(),
    /** What that price costs one buy on this venue at its compute-unit limit, before the strategy's floor and cap (lamports). */
    buyLamports: zCount.nullable(),
    /** Since the last good sample (ms). */
    ageMs: zCount.nullable(),
    /** Trades can't use it (older than `staleAfterMs`, or never sampled): dynamic fees pay the floor. */
    stale: z.boolean(),
    /** Non-zero per-slot fees behind the estimate. */
    samples: zCount,
    /** The latest sampling error (RPC URLs redacted by the engine). */
    lastError: z.string().nullable(),
});
/** `priorityFees` of the engine heartbeat (Valkey `engine:status`): the dynamic priority-fee estimator. */
export const zPriorityFees = z.object({
    /** The percentile of recent per-slot fees used as the estimate (75). */
    percentile: z.number().int().min(0).max(100),
    staleAfterMs: zCount,
    sampling: z.enum(PRIORITY_FEE_SAMPLING),
    /** The sampling loop's current interval (ms). */
    nextSampleMs: zCount,
    /** pump.fun bonding-curve trades. */
    curve: zVenuePriorityFee,
    /** PumpSwap pool trades. */
    pool: zVenuePriorityFee,
    /** Dynamic-fee transactions priced at the floor because the estimate was stale or missing (since the engine started). */
    floorFallbacks: zCount,
});
//# sourceMappingURL=system.js.map